import { useLazyQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { Autocomplete } from '@flixbus/honeycomb-react';

import { SEARCH_CITY_BY_NAME } from 'common/LocationSearch/qql/locationSearch';

/**
 * Single-city autocomplete picker over `searchCitiesByName`. Companion to
 * `CrewMemberAutocompleteSingleV2` — same shape, same debounce+lazy-query
 * pattern, same Honeycomb `Autocomplete` under the hood.
 *
 * @param {{
 *   defaultValue?: { id: string, name: string } | null,
 *   onSelection: (selection: { id: string, name: string }) => void,
 *   onReset?: () => void,
 *   label?: string,
 *   placeholder?: string,
 *   debounce?: number,
 * }} props
 */
const CityAutocompleteSingleV2 = ({
	defaultValue = null,
	onSelection,
	label = 'Search city',
	placeholder = 'Start typing a city name',
	optionsToDisplay = 5,
	debounce: debounceDuration = 300,
	onReset = () => {},
}) => {
	const [inputValue, setInputValue] = useState(defaultValue?.name ?? '');
	const [options, setOptions] = useState([]);

	const [getCitiesByName, { loading }] = useLazyQuery(SEARCH_CITY_BY_NAME, {
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	const selectedValue = useMemo(() => {
		if (!defaultValue?.id || !defaultValue?.name) return [];
		return [{ title: defaultValue.name, value: defaultValue.id }];
	}, [defaultValue]);

	const handleInputChange = (event) => {
		setInputValue(event.target.value);
	};

	const handleDebounce = async (typedValue) => {
		if (!typedValue) {
			setOptions([]);
			return [];
		}
		try {
			const { data } = await getCitiesByName({ variables: { cityName: typedValue } });
			const mapped = (data?.searchCitiesByName ?? []).map((city) => ({
				title: city.name,
				value: city.id,
				data: city,
			}));
			setOptions(mapped);
			return mapped;
		} catch {
			setOptions([]);
			return [];
		}
	};

	const handleChange = (selectedOptions) => {
		const next = Array.isArray(selectedOptions) ? selectedOptions : [];
		if (next.length === 0) {
			onReset();
			setInputValue('');
			return;
		}
		const selected = next[0];
		onSelection({ id: selected.value, name: selected.title });
		setInputValue(selected.title);
	};

	const handleClear = () => handleChange([]);

	return (
		<div>
			<Autocomplete
				value={selectedValue}
				inputValue={inputValue}
				options={options}
				debounce={debounceDuration}
				onDebounce={handleDebounce}
				onChange={handleChange}
				comboboxProps={{ loading, label, placeholder, onChange: handleInputChange }}
				listBoxProps={{ label: 'Cities', optionsToDisplay }}
			/>
			{selectedValue.length > 0 && (
				<button type="button" aria-label="Clear selection" onClick={handleClear} />
			)}
		</div>
	);
};

export default CityAutocompleteSingleV2;
